import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';

import type { CustomPeriodPickerOptions, GeneralComponentClass, Service } from 'webapp-types';

import eq from 'webapp/helpers/eq';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import type { Category, Item } from 'webapp/components/form/range/list-base';
import ToolTipComponent from 'webapp/components/tool-tip';
import ToolTipWithIconComponent from 'webapp/components/tool-tip-with-icon';

import type DateRange from 'webapp/models-custom/date-range';
import type Granulation from 'webapp/models-custom/granulation';

export interface Args {
	model: Category;
	itemComponent: GeneralComponentClass;
	openCategoryId: number | string | null;
	selectedItem: Item | null;
	toggleCategory: (categoryId: number | string) => void;
	selectItem: (item: Item, skipClose?: boolean) => void;
	theme?: string;
	hideUnsupported?: boolean;
	customPeriodPickerOptions?: CustomPeriodPickerOptions;
}

export default class FormRangeCategoryBaseComponent extends Component<Args> {
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;

	get isOpen() {
		return this.args.model.id === this.args.openCategoryId;
	}

	// when just one item inside, and it is disabled, we dont allow to open the category
	// and also show an info tooltip at the category (like we would on item)
	get isDisabled() {
		return this.args.model.items.length === 1 && this.args.model.items[0]?.isDisabled;
	}

	get shownItems() {
		let shownItems = this.args.model.items;
		if (this.args.hideUnsupported) {
			shownItems = shownItems.filter((item) => !item.isDisabled);
		}
		return shownItems;
	}

	@cached
	get limitationInfo() {
		let limitationInfo;
		if (this.isDisabled) {
			const firstLimitation = this.args.model.items[0] as DateRange | Granulation; // compares are not disabled
			if (firstLimitation) {
				limitationInfo = this.mmMetricsService.getLimitationInfoForDateRangeOrGranulation(
					firstLimitation,
					this.currentUserService.isAdmin,
				);
			}
		}
		return limitationInfo;
	}

	@action
	toggleCategory() {
		if (!this.isOpen && this.isDisabled) {
			return;
		}
		this.args.toggleCategory(this.args.model.id);
	}

	@action
	selectItem(item: Item, skipClose = false) {
		this.args.selectItem(item, skipClose);
	}
	<template>
		<li
			class="category-ct
				{{if @model.isDatepicker 'category-ct--datepicker'}}
				{{if @model.isSingleItem 'single'}}
				{{if this.isOpen 'open'}}
				{{if this.isDisabled 'disabled'}}
				{{this.limitationInfo.className}}"
		>
			<a class="category-title" {{on "click" this.toggleCategory}}>
				{{#if @model.isDatepicker}}
					<CommonUiIconComponent @name="Calendar" @size="xs" @class="calendar-icon" />
				{{/if}}
				<span>
					{{@model.title}}
					{{#if this.limitationInfo.text}}
						<ToolTipComponent
							@text={{this.limitationInfo.text}}
							@interactive={{true}}
							@maxWidth={{250}}
							@theme={{@theme}}
						/>
					{{/if}}
				</span>
				{{#if this.isOpen}}
					<CommonUiIconComponent @name="ChevronUp" @size="sm" />
				{{else}}
					<CommonUiIconComponent @name="ChevronDown" @size="sm" />
				{{/if}}
				{{#if this.limitationInfo.text}}
					<ToolTipWithIconComponent
						@text={{this.limitationInfo.text}}
						@maxWidth={{250}}
						@iconName={{this.limitationInfo.icon}}
						@theme={{@theme}}
					/>
				{{/if}}
			</a>
			<ul class="category">
				{{#each this.shownItems as |item|}}
					{{#let (component @itemComponent) as |ComponentName|}}
						<ComponentName
							@model={{item}}
							@theme={{@theme}}
							@isSelected={{eq @selectedItem item}}
							@hideUnsupported={{@hideUnsupported}}
							@onSelect={{this.selectItem}}
							@customPeriodPickerOptions={{@customPeriodPickerOptions}}
						/>
					{{/let}}
				{{/each}}
			</ul>
		</li>
	</template>
}
